import React from 'react';
import { Briefcase, Building2, Shield, Sunset, SearchX, HelpCircle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import SelectOption from '../ui/SelectOption';
import Disclaimer from '../ui/Disclaimer';
import DisclaimerContent from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

const EMPLOYMENT_STATUSES = [
  { value: 'employed_full_time', icon: Briefcase },
  { value: 'employed_part_time', icon: Clock },
  { value: 'self_employed', icon: Building2 },
  { value: 'military', icon: Shield },
  { value: 'employed', icon: Briefcase },
  { value: 'retired', icon: Sunset },
  { value: 'not_employed', icon: SearchX },
  { value: 'other', icon: HelpCircle },
] as const;

type EmploymentStatusFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function EmploymentStatusForm({ formData, onSubmit }: EmploymentStatusFormProps) {
  const { t } = useTranslation('wizard');
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();

  return (
    <div id="employment-status-form" className="space-y-6">
      <div className="text-center">
        <Title 
          id="employment-status-title"
          name="employment-status-title"
          as="h1" 
          className="mb-2"
        >
          {t('employmentStatus.title')}
        </Title>
        <Description 
          id="employment-status-description"
          name="employment-status-description"
          size="lg"
        >
          {t('employmentStatus.description')}
        </Description>
      </div>

      <div className="grid gap-3">
        {EMPLOYMENT_STATUSES.map(({ value, icon }) => (
          <SelectOption
            key={value}
            id={`employment-status-option-${value}`}
            name={`employment-status-option-${value}`}
            icon={icon}
            label={t(`employmentStatus.options.${value}.label`)}
            description={t(`employmentStatus.options.${value}.description`)}
            onClick={() => onSubmit({ employmentStatus: value })}
          />
        ))}
      </div>

      {showDisclaimer && (
        <Disclaimer
          id="employment-status-disclaimer"
          name="employment-status-disclaimer"
        >
          <DisclaimerContent />
        </Disclaimer>
      )}
    </div>
  );
}